import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MinusCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE = '__free__';
const EMPTY = { category: 'gasolina', amount: '', description: '', employee_id: '', type_id: '' };

const Pick = ({ label, value, onChange, items, placeholder, testid }) => (
  <div>
    <Label className="text-xs">{label} *</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const ExpenseDialog = ({ open, onClose }) => {
  const [f, setF] = useState(EMPTY);
  const [cfg, setCfg] = useState({ types: [], employees: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(EMPTY);
    axios.get(`${API}/expense-config`, { withCredentials: true }).then(r => setCfg(r.data)).catch(() => {});
  }, [open]);

  const isFree = f.category === 'otros' && f.type_id === FREE;
  const [differs, setDiffers] = useState(false);
  const [paidWeek, setPaidWeek] = useState(false);
  const set = (k) => (v) => setF(s => ({ ...s, [k]: v }));

  useEffect(() => {
    setDiffers(false);
    setPaidWeek(false);
    const amt = parseFloat(f.amount);
    if (f.category !== 'salario' || !f.employee_id) return undefined;
    const t = setTimeout(() => {
      axios.post(`${API}/expense-config/check-salary`, { employee_id: f.employee_id, amount: amt || 0 }, { withCredentials: true })
        .then(r => { setDiffers(!!amt && !r.data.matches); setPaidWeek(!!r.data.paid_this_week); }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [f.category, f.employee_id, f.amount]);

  const submit = async () => {
    const amt = parseFloat(f.amount);
    if (!amt || amt <= 0) return toast.error('Monto inválido');
    if (f.category === 'salario' && !f.employee_id) return toast.error('Selecciona el empleado');
    if (f.category === 'otros' && !f.type_id) return toast.error('Selecciona el tipo de gasto');
    if (isFree && !f.description.trim()) return toast.error('Describe el gasto');
    setSaving(true);
    try {
      await axios.post(`${API}/expenses`, {
        category: f.category, amount: amt, description: f.description || null,
        employee_id: f.category === 'salario' ? f.employee_id : null,
        expense_type_id: f.category === 'otros' && !isFree ? f.type_id : null,
      }, { withCredentials: true });
      toast.success('Gasto registrado');
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al registrar gasto'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-700 flex items-center gap-2"><MinusCircle size={22} weight="bold" /> Registrar gasto</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Pick label="Tipo de gasto" value={f.category} testid="expense-category"
            onChange={(v) => setF({ ...EMPTY, category: v })}
            items={[['salario', 'Salario'], ['pago_envios', 'Pago de envíos'], ['gasolina', 'Gasolina'], ['otros', 'Otros']]} />
          {f.category === 'salario' && (cfg.employees.length === 0
            ? <p className="text-xs text-rose-600">El administrador aún no ha registrado empleados en Configuración.</p>
            : <Pick label="Empleado" value={f.employee_id} placeholder="Selecciona…" testid="expense-employee"
                onChange={set('employee_id')}
                items={cfg.employees.map(e => [e.id, e.name])} />)}
          {paidWeek && <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="expense-paid-week">🔁 Este empleado ya recibió un pago de salario esta semana. Si registras otro, se avisará al administrador.</p>}
          {f.category === 'otros' && (
            <Pick label="¿Qué gasto es?" value={f.type_id} placeholder="Selecciona…" testid="expense-type" onChange={set('type_id')}
              items={[...cfg.types.map(t => [t.id, t.name]), [FREE, 'Otro (escribir nota)']]} />
          )}
          <div>
            <Label className="text-xs">Monto *</Label>
            <Input type="number" step="0.01" min="0" value={f.amount} onChange={(e) => set('amount')(e.target.value)} placeholder="0.00" data-testid="expense-amount" />
            {differs && <p className="text-xs text-amber-700 mt-1" data-testid="expense-salary-diff">⚠️ Este monto no coincide con el registrado. Solicita el monto al administrador; si lo registras así, se le avisará.</p>}
          </div>
          <div>
            <Label className="text-xs">Nota {isFree && <span className="text-rose-500">*</span>}</Label>
            <Input value={f.description} onChange={(e) => set('description')(e.target.value)} placeholder={isFree ? 'Describe el gasto' : 'Opcional'} data-testid="expense-description" />
          </div>
          <p className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2">
            💡 El gasto se reflejará en "Faltantes" del corte actual y descontará el efectivo esperado.{f.category === 'otros' && ' Se avisará al administrador por Telegram.'}
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="submit-expense">{saving ? 'Guardando…' : 'Registrar gasto'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
